import { useEffect, useRef } from 'react';
import { formatMonthYear } from '@betreuung/shared';
import { cn } from '@/lib/utils';

/** Grey separator row marking a change of month inside a long list. */
export function MonthDividerRow({ year, month }: { year: number; month: number }) {
  return (
    <div className="border-y border-divider bg-muted px-4 py-2 text-hint text-fg-hint">
      {formatMonthYear(year, month)}
    </div>
  );
}

/** Placeholder rows while the next page loads. */
export function SkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          className="flex items-center gap-2.5 border-b border-divider px-4 py-3.5 skeleton-pulse"
        >
          <div className="flex-1">
            <div className="h-[11px] w-[70%] rounded-md bg-divider" />
            <div className="mt-1.5 h-2 w-[30%] rounded bg-divider" />
          </div>
          <div className="h-[22px] w-16 rounded-chip bg-divider" />
        </div>
      ))}
    </>
  );
}

export interface InfiniteSentinelProps {
  onReach: () => void;
  /** Disabled while a page is in flight or the list is exhausted. */
  enabled: boolean;
  footer?: string;
}

/**
 * Triggers the next page slightly before the list end comes into view, so
 * scrolling stays continuous instead of stuttering at the bottom.
 */
export function InfiniteSentinel({ onReach, enabled, footer }: InfiniteSentinelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onReachRef = useRef(onReach);
  onReachRef.current = onReach;

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReachRef.current();
      },
      { rootMargin: '600px 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled]);

  return (
    <div ref={ref} className={cn('px-4 py-2.5 text-center text-hint text-fg-hint')}>
      {enabled && footer ? footer : null}
    </div>
  );
}

/** Empty-state and error rows shared by the list tabs. */
export function ListMessage({ children }: { children: string }) {
  return <p className="px-4 py-8 text-center text-body text-fg-muted">{children}</p>;
}
